import monday from 'monday-sdk-js';

import { API_ROUTES } from '../constants/api-routes';
import { getMondayToken } from './token';
import { resolveBoardForColumnPickers } from './resolveBoardForColumnPickers';

const API_URL = import.meta.env.VITE_GETSIGN_API_URL;

/** Env-aligned version for classic boards (`columns(types: …)` without capabilities). */
export const CLASSIC_BOARD_SCHEMA_API_VERSION = '2025-10';

/** API version required for `columns(capabilities: …)` on multi-level boards. */
export const MLS_BOARD_SCHEMA_API_VERSION = '2026-07';

const BOARD_COLUMN_FIELDS = `
  id
  settings_str
  settings
  title
  type
  archived
`;

const GET_BOARD_COLUMNS_FROM_ITEM_ID_CLASSIC_QUERY = `
  query GET_BOARD_COLUMNS_FROM_ITEMID_CLASSIC($ids: [ID!], $columnTypes: [ColumnType!]) {
    items(ids: $ids) {
      id
      subitems {
        id
        board {
          id
          columns(types: $columnTypes) {
            ${BOARD_COLUMN_FIELDS}
          }
        }
      }
    }
  }
`;

const GET_BOARD_COLUMNS_FROM_ITEM_ID_MLS_QUERY = `
  query GET_BOARD_COLUMNS_FROM_ITEMID_MLS($ids: [ID!], $columnTypes: [ColumnType!]) {
    items(ids: $ids) {
      id
      board {
        id
        columns(capabilities: [null, CALCULATED, VISIBILITY], types: $columnTypes) {
          ${BOARD_COLUMN_FIELDS}
        }
      }
      subitems {
        id
        board {
          id
          columns(capabilities: [null, CALCULATED, VISIBILITY], types: $columnTypes) {
            ${BOARD_COLUMN_FIELDS}
          }
        }
      }
    }
  }
`;

export async function fetchItemBoardHierarchy(itemId) {
  const sessionToken = localStorage.getItem('sessionToken');
  if (!sessionToken || itemId == null || itemId === '') {
    return null;
  }

  try {
    const path = API_ROUTES.MONDAY_ITEM_BOARD_HIERARCHY.replace(
      ':itemId',
      encodeURIComponent(String(itemId)),
    );
    const res = await fetch(`${API_URL}/${path}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        sessionToken,
      },
    });
    if (!res.ok) {
      return null;
    }
    const body = await res.json();
    return body?.data ?? null;
  } catch {
    return null;
  }
}

export const getItemDetails = async (fileId, itemId) => {
  try {
    const sessionToken = localStorage.getItem('sessionToken');

    const res = await fetch(
      `${API_URL}/monday/item-details/${fileId}/${itemId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          sessionToken: sessionToken,
        },
      },
    );
    if (res.status === 401) {
      return res;
    }
    return res.json();
  } catch (error) {}
};

export const getSubItems = async (itemId) => {
  const sessionToken = localStorage.getItem('sessionToken');

  const res = await fetch(`${API_URL}/monday/item-sub-items/${itemId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      sessionToken: sessionToken,
    },
  });

  return res.json();
};

/**
 * Status (and other) columns for SubItemFilters — classic subitem board or MLS parent board.
 * Uses backend hierarchy routing when available; classic boards stay on 2025-10 GraphQL.
 */
export const getBoardColumnsFromItemId = async (itemId, columnTypes) => {
  try {
    const mondayToken = await getMondayToken();
    if (!mondayToken?.token) {
      return undefined;
    }

    const mondayClient = monday();
    mondayClient.setToken(mondayToken.token);

    const variables = {
      ...(itemId != null && itemId !== '' && { ids: [String(itemId)] }),
      ...(columnTypes?.length && { columnTypes }),
    };

    const hierarchy = await fetchItemBoardHierarchy(itemId);
    const useMlsSchema = hierarchy?.usesMultiLevelSchema === true;

    const query = useMlsSchema
      ? GET_BOARD_COLUMNS_FROM_ITEM_ID_MLS_QUERY
      : GET_BOARD_COLUMNS_FROM_ITEM_ID_CLASSIC_QUERY;
    const apiVersion = useMlsSchema
      ? MLS_BOARD_SCHEMA_API_VERSION
      : CLASSIC_BOARD_SCHEMA_API_VERSION;

    const res = await mondayClient.api(query, {
      variables,
      apiVersion,
    });

    return resolveBoardForColumnPickers(res?.data?.items?.[0]);
  } catch (err) {
    console.log('Error while fetching board columns', err);
  }
};

export const getBatchedSubitemFormula = async (
  itemId,
  batch_number,
  batch_size,
) => {
  const url = `${API_URL}/monday/batch-hydrate-formula-columns/${itemId}?batch_number=${batch_number}&batch_size=${batch_size}`;

  const sessionToken = localStorage.getItem('sessionToken');
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      sessionToken: sessionToken,
    },
  });

  return res.json();
};
